import React, { useCallback } from 'react'
import { useDeleteCurricula } from '../../tree/TreeActions'
import ThreeSixtyIcon from '@mui/icons-material/ThreeSixty'
import { Box, List, ListItem, ListItemText } from '@mui/material'
import { ICurricula } from 'infrastructure/api'
import DeleteIcon from '@mui/icons-material/Delete'
import { ConfirmWithDialogButton } from 'shared/ui'
import { ReIndexButton } from '../tool_components/ReIndexButton'
import { ProductType } from '../products/types'

export default function CurriculumRow({
  product,
  curriculumCopy,
  onDeleted,
}: {
  product: ProductType
  curriculumCopy: ICurricula
  onDeleted?: () => void
}) {
  return (
    <Box key={curriculumCopy.curriculum_id}>
      <Box textAlign={'start'} width={'100%'}>
        <List>
          <ListItem>
            <ListItemText
              primary={`Title: ${product.title}`}
              secondary={`ID: ${curriculumCopy.curriculum_id}`}
            />
            <CurriculaActions
              curricula={curriculumCopy}
              onDeleted={onDeleted}
            />
          </ListItem>
        </List>
      </Box>
    </Box>
  )
}

function CurriculaActions({
  curricula,
  onDeleted,
}: {
  curricula: ICurricula
  onDeleted?: () => void
}) {
  return (
    <>
      <Box mb={3}>
        <NotDeletedCurriculaActions
          curricula={curricula}
          onDeleted={onDeleted}
        />
      </Box>
      <Box>
        <DeletedCurriculaActions curricula={curricula} onDeleted={onDeleted} />
      </Box>
    </>
  )
}

function useSetDeleted(onDeleted?: () => void) {
  const deleteCurricula = useDeleteCurricula()
  return useCallback(
    async (curricula: ICurricula, deleted: boolean) => {
      await deleteCurricula(curricula, deleted)
      onDeleted?.()
    },
    [deleteCurricula, onDeleted],
  )
}

function DeletedCurriculaActions({
  curricula,
  onDeleted,
}: {
  curricula: ICurricula
  onDeleted?: () => void
}) {
  const doDelete = useSetDeleted(onDeleted)

  if (!curricula.deleted) return null
  return (
    <ConfirmWithDialogButton
      dialogTitle={'Are you sure you want to undelete this curriculum?'}
      dialogContent={'Attempt to undelete'}
      onClick={() => doDelete(curricula, false)}
    >
      <ThreeSixtyIcon />
    </ConfirmWithDialogButton>
  )
}

function NotDeletedCurriculaActions({
  curricula,
  onDeleted,
}: {
  curricula: ICurricula
  onDeleted?: () => void
}) {
  const doDelete = useSetDeleted(onDeleted)

  if (curricula.deleted) return null
  return (
    <>
      <ConfirmWithDialogButton
        dialogTitle={'Are you sure you want to delete this curriculum?'}
        dialogContent={`These changes probably can't be undone`}
        onClick={() => doDelete(curricula, true)}
      >
        <DeleteIcon />
      </ConfirmWithDialogButton>
      <ReIndexButton
        treeIds={{
          treeId: curricula.tree_id,
          versionId: curricula.tree_version,
        }}
      />
    </>
  )
}
